import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import PersonalDetailsSection from '../components/profile/PersonalDetailsSection';
import CredentialsSection from '../components/profile/CredentialsSection';
import { profileAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ONBOARDING_STEPS, onboardingHref } from '../features/home/onboardingSteps';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const isOnboarding = searchParams.get('onboarding') === '1';

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingName, setSavingName] = useState(false);
  const [error, setError] = useState('');

  const loadProfile = useCallback(async () => {
    setError('');
    try {
      const res = await profileAPI.get();
      if (res.success) {
        setProfile(res.data);
      } else {
        setError(res.message || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveName = async (name) => {
    setSavingName(true);
    try {
      const res = await profileAPI.update({ name });
      if (!res.success) throw new Error(res.message || 'Failed to save name');
      setProfile((prev) => ({ ...prev, name }));
      await refreshUser();
    } finally {
      setSavingName(false);
    }
  };

  const handleSetPassword = async ({ newPassword, confirmPassword }) => {
    const res = await profileAPI.setPassword({ new_password: newPassword, confirm_password: confirmPassword });
    if (!res.success) throw new Error(res.message || 'Failed to set password');
    setProfile((prev) => ({
      ...prev,
      has_password: true,
      username: res.data?.username || prev?.phone || prev?.username,
    }));
    await refreshUser();
    if (isOnboarding) {
      const locationStep = ONBOARDING_STEPS.find((s) => s.id === 'location');
      if (locationStep) navigate(onboardingHref(locationStep));
    }
  };

  const handleChangePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
    const res = await profileAPI.changePassword({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    if (!res.success) throw new Error(res.message || 'Failed to change password');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto py-8 text-sm text-ga-text-muted">Loading profile…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="ga-card rounded-lg border border-ga-border-subtle/60 p-5">
          <h1 className="text-2xl font-bold text-ga-text-primary">My Profile</h1>
          <p className="text-ga-text-muted mt-1 text-sm">
            {isOnboarding
              ? 'Secure your account by setting a password before continuing setup.'
              : 'Manage your personal details and sign-in credentials.'}
          </p>
        </div>

        {error && (
          <p className="text-sm text-ga-danger" role="alert">{error}</p>
        )}

        {profile && (
          <>
            <PersonalDetailsSection
              profile={profile}
              onSaveName={handleSaveName}
              saving={savingName}
            />
            <CredentialsSection
              profile={profile}
              onSetPassword={handleSetPassword}
              onChangePassword={handleChangePassword}
            />
          </>
        )}

        {!profile && !error && user && (
          <p className="text-sm text-ga-text-muted">Could not load your profile.</p>
        )}
      </div>
    </DashboardLayout>
  );
}
